import React, { useState, useEffect, useCallback } from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import {
  ComposedChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  LabelList,
} from "recharts";

const formatAxisLabel = (value) => {
  if (value == null) return "";
  const str = String(value);

  if (/^\d+$/.test(str)) {
    return Number(str).toString();
  }

  // Try parsing as date
  const date = new Date(str);
  if (!isNaN(date) && str.length > 10) {
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    });
  }

  // Truncate long strings
  return str.length > 12 ? str.slice(0, 12) + "…" : str;
};;

const formatYValue = (value) => {
  if (value == null) return "";

  const num = Number(value);
  if (isNaN(num)) return value;

  return Math.ceil(num).toString();
};
const formatLabel = (key) => {
  if (!key) return "";
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const isDateString = (value) => {
  if (typeof value !== "string") return false;
  const date = new Date(value);
  return !isNaN(date.getTime());
};

const formatValue = (val) => {
  if (val == null || val === "") return "-";

  if (isDateString(val)) {
    const date = new Date(val);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  const cleanedValue = String(val).replace(/,/g, "");
  const num = Number(cleanedValue);

  if (!isNaN(num) && typeof val !== "boolean") {
    return String(Math.round(num));
  }

  return cleanedValue;
};
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const data = payload[0]?.payload || {};

  // Show every field except nested additional object itself
  const allFields = Object.entries(data).filter(
    ([key]) => key !== "additional",
  );

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E8ECF0",
        borderRadius: "8px",
        p: "10px 14px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        minWidth: 220,
        maxWidth: 350,
      }}
    >
      {/* All top-level fields */}
      {allFields.map(([key, value]) => (
        <Box
          key={key}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            py: 0.4,
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              color: "#4D5F6E",
              fontWeight: 500,
              flex: 1,
            }}
          >
            {formatLabel(key)}
          </Typography>

          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: "#1A2025",
              textAlign: "right",
            }}
          >
            {typeof value === "number"
              ? formatYValue(value)
              : formatValue(value)}
          </Typography>
        </Box>
      ))}

      {/* If additional object still exists, render dynamically */}
      {data.additional &&
        Object.entries(data.additional).map(([key, value]) => (
          <Box
            key={`additional-${key}`}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              py: 0.4,
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                color: "#4D5F6E",
                flex: 1,
              }}
            >
              {formatLabel(key)}
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: "#1A2025",
                textAlign: "right",
              }}
            >
              {typeof value === "number"
                ? formatYValue(value)
                : formatValue(value)}
            </Typography>
          </Box>
        ))}
    </Box>
  );
};;;;;;

const CustomChartSkeleton = () => (
  <Box
    sx={{
      width: "100%",
      height: { sm: 160, md: 200, lg: 300 },
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      p: 2,
    }}
  >
    <Box sx={{ display: "flex", height: { sm: 140, md: 180, lg: 280 } }}>
      <Box
        sx={{
          width: 50,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} variant="text" width={40} height={18} />
        ))}
      </Box>
      <Box sx={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 2 }}>
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            width="100%"
            height={100 + Math.random() * 120}
            sx={{ borderRadius: 1, flex: 1 }}
          />
        ))}
      </Box>
    </Box>
    <Stack direction="row" spacing={3} justifyContent="center" sx={{ mt: 2 }}>
      {[1, 2].map((i) => (
        <Stack key={i} direction="row" spacing={1} alignItems="center">
          <Skeleton variant="circular" width={10} height={10} />
          <Skeleton variant="text" width={70} height={16} />
        </Stack>
      ))}
    </Stack>
  </Box>
);

const BAR_COLOR = "#A3D9A5";
const LINE_COLOR = "#FF7043";
const AREA_COLOR = "#B6F7FF";

const CustomChartContent = ({
  chartData,
  barKey,
  lineKey,
  areaKey,
  xKey,
  threshold,
  thresholdColor = "#FF4D4F",
}) => {
  const safeXKey = xKey || "name";
  const barMinWidth = 80;
  const minChartWidth = chartData.length * barMinWidth;
  const hasThreshold = threshold != null && !isNaN(Number(threshold));

  // Build legend items from actual data keys
  const legendItems = [];
  if (barKey)
    legendItems.push({
      label: formatLabel(barKey),
      color: BAR_COLOR,
      type: "bar",
    });
  if (lineKey)
    legendItems.push({
      label: formatLabel(lineKey),
      color: LINE_COLOR,
      type: "line",
    });
  if (areaKey)
    legendItems.push({
      label: formatLabel(areaKey),
      color: AREA_COLOR,
      type: "area",
    });

  return (
    <>
      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          overflowY: "hidden",
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: "100%",
            minWidth: minChartWidth,
            height: { sm: 160, md: 200, lg: 300 },
          }}
        >
          <Box sx={{ p: "8px", width: "100%", height: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 16, right: 24, left: 8, bottom: 8 }}
                barCategoryGap="20%"
              >
                <CartesianGrid stroke="#E5E7EB" vertical={false} />

                <XAxis
                  dataKey={safeXKey}
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fill: "#4D5F6E",
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tickFormatter={formatAxisLabel}
                  tickMargin={8}
                />

                {/* Left Y-axis for bar */}
                <YAxis
                  yAxisId="left"
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fill: "#4D5F6E",
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                  width={75}
                  tickFormatter={formatYValue}
                  tickMargin={4}
                />

                {/* Right Y-axis for line (only when lineKey exists) */}
                {lineKey && (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fill: LINE_COLOR,
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                    width={75}
                    tickFormatter={formatYValue}
                    tickMargin={4}
                  />
                )}

                <Tooltip content={<CustomTooltip />} />

                {hasThreshold && (
                  <ReferenceLine
                    yAxisId="left"
                    y={Number(threshold)}
                    stroke={thresholdColor}
                    strokeDasharray="6 4"
                    strokeWidth={2}
                    label={{
                      value: `Threshold: ${formatYValue(threshold)}`,
                      position: "insideTopRight",
                      fill: thresholdColor,
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  />
                )}

                {areaKey && (
                  <Area
                    type="monotone"
                    dataKey={areaKey}
                    yAxisId="left"
                    fill={AREA_COLOR}
                    stroke={AREA_COLOR}
                    fillOpacity={0.2}
                    strokeWidth={0}
                  />
                )}

                <Bar
                  dataKey={barKey}
                  yAxisId="left"
                  barSize={36}
                  fill={BAR_COLOR}
                  radius={[6, 6, 0, 0]}
                >
                  <LabelList
                    dataKey={barKey}
                    position="top"
                    formatter={formatYValue}
                    style={{
                      fill: "#1A2025",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  />
                </Bar>

                {lineKey && (
                  <Line
                    type="monotone"
                    dataKey={lineKey}
                    yAxisId="right"
                    stroke={LINE_COLOR}
                    strokeWidth={2.5}
                    dot={{
                      r: 4,
                      fill: LINE_COLOR,
                      stroke: "#fff",
                      strokeWidth: 2,
                    }}
                    activeDot={{ r: 6 }}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>

      {/* Legend */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "12px 24px",
          pt: 1.5,
          pb: 0.5,
        }}
      >
        {legendItems.map((item, idx) => (
          <Box
            key={idx}
            sx={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            {item.type === "line" ? (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ width: 12, borderTop: `2px solid ${item.color}` }} />
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: item.color,
                  }}
                />
                <Box sx={{ width: 12, borderTop: `2px solid ${item.color}` }} />
              </Box>
            ) : (
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "3px",
                  bgcolor: item.color,
                  opacity: item.type === "area" ? 0.5 : 1,
                }}
              />
            )}
            <Box sx={{ fontSize: 12, color: "#4D5F6E", fontWeight: 500 }}>
              {item.label}
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
};;

const CustomComposedChart = ({
  data = [],
  barKey = "",
  lineKey = "",
  areaKey = "",
  xKey,
  refreshTrigger,
  threshold,
  thresholdColor,
  loading,
}) => {
  const [chartData, setChartData] = useState(data);

  useEffect(() => {
    setChartData(data);
  }, [data]);

  const handleRefresh = useCallback(() => {
    // setLoading(true);
    setTimeout(() => {
      setChartData([...data]);
    }, 1500);
  }, [data]);

  useEffect(() => {
    if (refreshTrigger) {
      handleRefresh();
    }
  }, [refreshTrigger, handleRefresh]);

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: 4,
        height: "100%",
      }}
    >
      {loading ? (
        <CustomChartSkeleton />
      ) : (
        <CustomChartContent
          chartData={chartData}
          barKey={barKey}
          lineKey={lineKey}
          areaKey={areaKey}
          xKey={xKey}
          threshold={threshold}
          thresholdColor={thresholdColor}
        />
      )}
    </Box>
  );
};

export default CustomComposedChart;